// Groups the tool calls into a card that matches the provider cards beside it.

import { useState } from 'react';
import { Wrench } from 'lucide-react';
import { INTEGRATIONS } from '@/modules/integrations/constants';
import type { IntegrationType } from '@/modules/integrations/types';
import { cn } from '@/lib/utils';
import { CollapseBody, CollapseHeader } from './collapse-toggle';
import { ToolCallCard } from './tool-call-card';
import type { TraceStep } from './types';

/** Brahma names a few tools after the product area, not the integration. */
const PROVIDER_ALIASES: Record<string, IntegrationType> = { yotpo_loyalty: 'yotpo' };

/** Names the card after the integration, only when every call agrees on one. */
function cardTitle(steps: TraceStep[]): string {
  const slugs = new Set(steps.map((step) => step.toolProvider?.toLowerCase() ?? ''));
  const [slug] = [...slugs];
  if (slugs.size !== 1 || !slug) return 'Tool Calls';
  const known = INTEGRATIONS[PROVIDER_ALIASES[slug] ?? (slug as IntegrationType)];
  return known?.name ?? 'Tool Calls';
}

export function ToolCallsCard({ steps }: { steps: TraceStep[] }) {
  const [open, setOpen] = useState(true);
  const title = cardTitle(steps);

  return (
    <div className="flex w-full flex-col border-l-4 border-solid border-gray-800 bg-gray-100">
      <div className={cn('transition-[padding] duration-300 ease-in-out motion-reduce:transition-none flex w-full flex-col bg-white px-5 pt-4', open ? 'pb-5' : 'pb-4')}>
        <CollapseHeader open={open} label={title} variant="chevron" onToggle={() => setOpen((prev) => !prev)} className="h-5">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex size-5 shrink-0 items-center justify-center rounded bg-text-prose text-white">
              <Wrench className="size-3.5" />
            </span>
            <p className="truncate text-[14px] font-semibold leading-[1.1] text-text-body">{title}</p>
          </div>
        </CollapseHeader>
      </div>

      <CollapseBody open={open}>
        <div className="flex w-full flex-col gap-3 px-4 py-3">
          <div className="flex w-full flex-col rounded-xl border border-gray-100 bg-white drop-shadow-[0_0_3px_rgba(0,0,0,0.06)]">
            {steps.map((step, position) => (
              <div key={step.id} className="flex w-full flex-col">
                {position > 0 && <div className="h-px w-full bg-gray-500" />}
                <ToolCallCard step={step} />
              </div>
            ))}
          </div>
        </div>
      </CollapseBody>
    </div>
  );
}
